import {
    useCallback, useMemo,
} from 'react'
import Typography from '@mui/material/Typography'

import RichTable from 'app/shared-components/RichTableReduxWrapper'
import Card from 'app/shared-components/Card'
import {
    PrintPreviewUniq,
} from 'app/Apps/Pricing/ContractBasis/utils/createQuote.types'
import useEnumValues from 'app/hooks/useEnumValues'
import EnumSelector from 'app/shared-components/EnumSelector'
import {
    LaneQuoteViewType,
} from 'app/enums/label.enums'
import useLabel from 'app/hooks/useLabel'

import useStyles from './PrintPreviewDataForm.styles'
import {
    additionalFeesColumns,
    laneFeesColumns,
} from './PrintPreviewDataForm.columns'

type Props = {
    data: PrintPreviewUniq,
    setData: (callback: (prev: PrintPreviewUniq) => PrintPreviewUniq) => void
    laneFeesSelectedRows: string[],
    laneFeesOnSelectedRow: (selectedRows: string[]) => void,
    additionFeesSelectedRows: number[],
    additionalFeesOnSelectedRow: (selectedRows: number[]) => void,
}

const PrintPreviewDataForm = ({
    laneFeesSelectedRows,
    laneFeesOnSelectedRow,
    additionFeesSelectedRows,
    additionalFeesOnSelectedRow,
    data,
    setData,
}: Props) => {
    const l = useLabel()
    const getAllowedValues = useEnumValues()
    const {
        classes,
    } = useStyles()
    const {
        additionalFeesTableRows,
        lanesTableRows,
    } = data

    const additionalFeesLoad = useCallback(() => {
        return Promise.resolve({
            data: additionalFeesTableRows,
        })
    }, [additionalFeesTableRows])

    const laneFeesLoad = useCallback(() => {
        return Promise.resolve({
            data: lanesTableRows,
        })
    }, [lanesTableRows])

    const handleAdditionalFeesOnSelectedRow = useCallback((selectedRowsIds) => {
        additionalFeesOnSelectedRow(selectedRowsIds)
    }, [additionalFeesOnSelectedRow])

    const handleLaneFeesOnSelectedRow = useCallback((selectedRowsIds) => {
        laneFeesOnSelectedRow(selectedRowsIds)
    }, [laneFeesOnSelectedRow])

    const handleSelectFeesView = useCallback((laneQuoteViewType: LaneQuoteViewType) => {
        setData((prev) => {
            return {
                ...prev,
                laneQuoteViewType,
            }
        })
    }, [setData])

    const allowedFeesViewValues = useMemo(() => {
        return getAllowedValues(LaneQuoteViewType)
    }, [getAllowedValues])

    return (
        <div>
            <Card
                fullHeight
                contentClass={classes.cardContent}
                data-testid="createquote-additionalFees-card"
            >
                <>
                    <div className={classes.header}>
                        <Typography variant="h3">
                            Lane / Lane Fees
                        </Typography>
                    </div>
                    <RichTable
                        configName="CreateQuote.LaneFees"
                        name="LaneFees"
                        load={laneFeesLoad}
                        columns={laneFeesColumns}
                        uniqField="tempId"
                        className={classes.tableContent}
                        classNames={{
                            contentWrapper: classes.tableContentWrapper,
                        }}
                        selectedRows={laneFeesSelectedRows}
                        onSelectRow={handleLaneFeesOnSelectedRow}
                        showSelectAll
                    />
                </>
            </Card>
            <Card
                fullHeight
                contentClass={classes.cardContent}
                data-testid="createquote-additionalFees-card"
            >
                <>
                    <div className={classes.header}>
                        <Typography variant="h3">
                            Additional Fees
                        </Typography>
                    </div>
                    <RichTable
                        configName="CreateQuote.AdditionalFees"
                        name="AdditionalFees"
                        load={additionalFeesLoad}
                        columns={additionalFeesColumns}
                        uniqField="additionalFeesFeeId"
                        className={classes.tableContent}
                        classNames={{
                            contentWrapper: classes.tableContentWrapper,
                        }}
                        onSelectRow={handleAdditionalFeesOnSelectedRow}
                        selectedRows={additionFeesSelectedRows}
                        showSelectAll
                    />
                </>
            </Card>
            <Card contentClass={classes.cardContent}>
                <div className={classes.header}>
                    <Typography variant="h3">
                        Fees In Quote PDF
                    </Typography>
                </div>
                <EnumSelector
                    title={l('AccountQuotes.laneQuoteViewType')}
                    onChange={handleSelectFeesView}
                    allowedValues={allowedFeesViewValues}
                    value={data?.laneQuoteViewType}
                    noSort
                />
            </Card>
        </div>
    )
}

export default PrintPreviewDataForm
